/* eslint-disable complexity */
import { useEffect, useRef, useState } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { MostWantedFilled } from 'baby-design/icons'
import { Tag, TextLink } from 'baby-design'

import ScrollLockModal from 'shared/ScrollLockModal'
import { RegItemBuyingOptionsModalProps } from './RegItemBuyingOptionsModal.types'
import css from './RegItemBuyingOptionsModal.styles.scss'
import useGetRegItem from '../../hooks/useGetRegItem'
import RegItemBuyingOptionsModalSkeleton from './RegItemBuyingOptionsModal.skeleton'
import {
  getDetailsText,
  getIsRegItemDetailsVisible,
  getIsShowMoreVisible,
  getRegItemBuyingOptionsComponent,
  getRegItemBuyingOptionsTitle,
} from './RegItemBuyingOptionsModal.utils'

const RegItemBuyingOptionsModal = NiceModal.create(
  ({ regItemId, registry }: RegItemBuyingOptionsModalProps) => {
    const modal = useModal()
    const regItemDescriptionRef = useRef<HTMLDivElement>(null)
    const { regItem, regItemQuery } = useGetRegItem(regItemId, registry.id)
    const [isShowMoreVisible, setIsShowMoreVisible] = useState(false)
    const [isDetailsCollapsed, setIsDetailsCollapsed] =
      useState(isShowMoreVisible)
    const RegItemBuyingOptions = getRegItemBuyingOptionsComponent(regItem)
    const isRegItemDetailsVisible = getIsRegItemDetailsVisible(regItem)
    const detailsText = getDetailsText(regItem, isDetailsCollapsed)
    const title = getRegItemBuyingOptionsTitle(regItem)

    useEffect(() => {
      const isMoreToggleVisible = getIsShowMoreVisible(regItem)
      setIsShowMoreVisible(isMoreToggleVisible)
      setIsDetailsCollapsed(isMoreToggleVisible)
    }, [regItem])

    return (
      <ScrollLockModal
        showCloseButton
        className={css.RegItemBuyingOptionsModal__ModalOverride}
        handleClose={modal.hide}
        isOpen={modal.visible}
        mobileVariant="basic"
        title={title}
      >
        <div
          className={css.RegItemBuyingOptionsModal}
          data-testid="RegItemBuyingOptionsModal"
        >
          {regItemQuery.isLoading && <RegItemBuyingOptionsModalSkeleton />}
          {regItem && (
            <>
              <div className={css.RegItemBuyingOptionsModal__ProductDetails}>
                <div className={css.RegItemBuyingOptionsModal__ProductImage}>
                  <img alt={regItem.title} src={regItem.imgUrl} />
                </div>

                <div className={css.RegItemBuyingOptionsModal__ProductInfo}>
                  <div className={css.RegItemBuyingOptionsModal__ProductTitle}>
                    {regItem.title}
                  </div>
                  <TextLink
                    underline
                    href={regItem.url}
                    size="sm"
                    target="_blank"
                    weight="regular"
                  >
                    View details
                  </TextLink>
                  <div className={css.RegItemBuyingOptionsModal__ProductTags}>
                    {regItem.isFavorite && (
                      <Tag
                        LeftIcon={
                          <MostWantedFilled
                            className={
                              css.RegItemBuyingOptionsModal__MostWantedIcon
                            }
                          />
                        }
                        size="md"
                        type="primary"
                        variant="normal"
                      >
                        Most Wanted
                      </Tag>
                    )}
                  </div>
                </div>
              </div>

              {isRegItemDetailsVisible && (
                <div className={css.RegItemBuyingOptionsModal__RegItemDetails}>
                  {regItem.quantityNeeded > 1 && (
                    <div
                      className={css.RegItemBuyingOptionsModal__RegItemQuantity}
                    >
                      <span className={css.RegItemBuyingOptionsModal__SubTitle}>
                        Quantity needed:{' '}
                      </span>
                      {regItem.quantityNeeded}
                    </div>
                  )}
                  {regItem.description && (
                    <div
                      className={
                        css.RegItemBuyingOptionsModal__RegItemDescription
                      }
                      data-testid="RegItemDescription"
                      ref={regItemDescriptionRef}
                    >
                      <div className={css.RegItemBuyingOptionsModal__SubTitle}>
                        Note from {registry.firstNames}:
                      </div>
                      &ldquo;{detailsText}
                      {(!isShowMoreVisible || !isDetailsCollapsed) && (
                        <>&rdquo;</>
                      )}
                      {isShowMoreVisible && (
                        <div className={css.RegItemBuyingOptionsModal__More}>
                          <TextLink
                            size="md"
                            onClick={() =>
                              setIsDetailsCollapsed(!isDetailsCollapsed)
                            }
                          >
                            {isDetailsCollapsed ? 'More' : 'Less'}
                          </TextLink>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <RegItemBuyingOptions regItem={regItem} />
            </>
          )}
        </div>
      </ScrollLockModal>
    )
  }
)

export default RegItemBuyingOptionsModal
